import ReconciliationIcon from 'components/ReusableComponents/Icons/Reconciliation'
import Toggle from 'components/ReusableComponents/Toggle/index'
import ReconciliationInfoCard from 'features/BillingManagement/Card/ReconciliationInfoCard'
import EditBookingDialog from 'features/BillingManagement/EditBookingDialog'
import BookingHistory from 'features/BookingDetails/BookingHistory'
import moment from 'moment'
import { memo, useState } from 'react'

import { ReactComponent as CheckIcon } from '../../../img/risk-icons/check.svg'
import { ReactComponent as StarIcon } from '../../../img/risk-icons/star.svg'
import { ReactComponent as WarningIcon } from '../../../img/risk-icons/warning.svg'
import matchNames from '../../../main/bookingMatchNames'
import { formatPrice, titleCase } from '../../../utils/common'
import CallIcon from './CallIcon'
import styles from './Card.module.scss'
import ClickIcon from './ClickIcon'
import { BgText, ColoredText } from './ColoredText'
import HoverCard from './HoverCard'
import { ReactComponent as InfoIcon } from './info.svg'
import MessageIcon from './MessageIcon'
import { ReactComponent as SelectedIcon } from './selected.svg'
import { Line, Point, Timeline } from './Timeline'

const BeforeInviteLine = ({ days }) => {
  let text = days + ' days'
  let bgText = text

  if (days > 90) {
    bgText = <BgText level="good">{text}</BgText>
  } else if (days > 30) {
    bgText = <BgText level="nice">{text}</BgText>
  }

  return <Line form="solid" range={bgText} />
}

const Counts = ({ click = 0, message = 0, call = 0 }) => {
  return (
    <div className={styles.countsHolder}>
      <ClickIcon fill={click > 0 ? '#66bb6a' : '#A8ADB2'} width={22} height={22} />
      {click}
      <MessageIcon fill={message > 0 ? '#66bb6a' : '#A8ADB2'} width={22} height={22} />
      {message}
      <CallIcon fill={call > 0 ? '#66bb6a' : '#A8ADB2'} width={22} height={22} />
      {call}
    </div>
  )
}

const BeforeBookingLine = ({ days }) => {
  let text = days + ' days'
  let bgText = text

  if (days > 30) {
    bgText = <BgText level="terrible">{text}</BgText>
  } else if (days > 15) {
    bgText = <BgText level="bad">{text}</BgText>
  }

  return <Line form="solid" range={bgText} />
}

const PrevBookingPoint = ({
  checkout,
  days,
  handleClick,
  showAllPriorBookings,
  numberOfPriorsByName,
  numberOfPriorsByPhone,
  numberOfPriorsByPhoneAndName,
  numberOfPriorsTotal
}) => {
  const text =
    numberOfPriorsByName === 0
      ? 'not in last 12 mo'
      : numberOfPriorsByName + ' prior stays'

  function getLevelFromCount(count) {
    let level = 'good'

    if (count > 8) {
      level = 'terrible'
    } else if (count > 5) {
      level = 'bad'
    } else if (count === 0) {
      level = 'regular'
    }

    return level
  }

  return checkout ? (
    <>
      <Point
        primary={moment(checkout).format('MMM DD, YYYY')}
        secondary={
          showAllPriorBookings ? (
            <div className={styles.secondaryHolder}>
              <ColoredText
                onClick={handleClick}
                level={getLevelFromCount(numberOfPriorsByPhone)}
              >
                By only phone: {numberOfPriorsByPhone}
              </ColoredText>
              <ColoredText
                level={getLevelFromCount(numberOfPriorsByName)}
                onClick={handleClick}
              >
                By only name: {numberOfPriorsByName}
              </ColoredText>
              <ColoredText
                level={getLevelFromCount(numberOfPriorsByPhoneAndName)}
                onClick={handleClick}
              >
                Phone &amp; name: {numberOfPriorsByPhoneAndName}
              </ColoredText>
              <ColoredText
                level={getLevelFromCount(numberOfPriorsTotal)}
                onClick={handleClick}
              >
                Total: {numberOfPriorsTotal}
              </ColoredText>
            </div>
          ) : (
            <ColoredText
              level={getLevelFromCount(numberOfPriorsByPhone)}
              onClick={handleClick}
            >
              {text}
            </ColoredText>
          )
        }
      />
      <BeforeInviteLine days={days} />
    </>
  ) : (
    <>
      <Point primary="Unknown" color="empty" />
      <Line form="dotted" />
    </>
  )
}

const BillingCard = props => {
  const {
    handleCheck,
    handleMultiSelectClick,
    convertedClientName,
    conversionIsBasedOn,
    isInvoicingRejected,
    numberOfPriorsByPhone,
    numberOfPriorsByName,
    numberOfPriorsByPhoneAndName,
    numberOfPriorsTotal,
    previousCheckOut,
    invitedOn,
    daysWithoutBookingUntilInvite,
    convertedOn,
    daysFromInviteUntilBooking,
    checkIn,
    checkOut,
    lengthOfStay,
    convertedBookingSource,
    convertedTotal,
    convertedTrackingCode,
    convertedRateCode,
    convertedRateCodeKind,
    convertedRateCodeAddedBy,
    convertedRateCodeAddedOn,
    convertedBookingUser,
    convertedSubscriberHotelBookingId,
    convertedFormattedAddress,
    convertedPhoneNumber,
    convertedEmail,
    invoiceRejectReason,
    inviteSubscriptionId,
    isAgency,
    convertedBookingStatus,
    exclusions,
    isSelected,
    isEditable,
    setGuestDataToInspect,
    showAllPriorBookings,
    callCount,
    clickCount,
    messageCount,
    reconciliations,
    lastReconciliationStatus,
    reloadResults,
    agencyName
  } = props

  const isBlacklisted = exclusions.length
  const exclusionKeys = isBlacklisted
    ? exclusions.map(o => o.excludeCategory.toLowerCase())
    : []
  const isExcludedStrict = exclusions.some(o => o.excludeBehavior === 'NoChargeStrict')
  const isExcluded = (...keys) =>
    keys.some(key => exclusionKeys.includes(key.toLowerCase()))

  const [isShowingEditBookingDialog, setIsShowingEditBookingDialog] = useState(false)

  const [isGuestProfileVisible, setIsGuestProfileVisible] = useState(false)

  return (
    <div className={`${styles.card} ${isSelected ? styles.selectedCard : ''}`}>
      {isShowingEditBookingDialog && (
        <EditBookingDialog
          subscriberHotelBookingId={convertedSubscriberHotelBookingId}
          totalRate={convertedTotal}
          checkIn={checkIn}
          checkOut={checkOut}
          transactionStatus={convertedBookingStatus}
          onClose={() => setIsShowingEditBookingDialog(false)}
          reloadResults={reloadResults}
        />
      )}
      {isGuestProfileVisible && (
        <BookingHistory
          subscriberId={inviteSubscriptionId}
          onClose={() => setIsGuestProfileVisible(false)}
        />
      )}
      {isSelected && (
        <SelectedIcon onClick={handleMultiSelectClick} className={styles.selectedIcon} />
      )}
      <div className={styles.head}>
        <p
          className={inviteSubscriptionId ? styles.guestNameLink : styles.guestName}
          onClick={() => (inviteSubscriptionId ? setIsGuestProfileVisible(true) : null)}
        >
          {titleCase(convertedClientName)}
        </p>
        <div className={styles.matchCriteria}>
          <>
            {conversionIsBasedOn === 'PartialBookingGuestName' && <WarningIcon />}
            {conversionIsBasedOn === 'PhoneNumber' && <StarIcon />}
            {conversionIsBasedOn === 'Email' && <CheckIcon />}
            <span className={styles.matchName}>{matchNames[conversionIsBasedOn]}</span>
          </>
        </div>
        {isBlacklisted ? (
          <div className={styles.blacklistedTextHolder}>
            <span className={styles.blacklistedText}>
              Excluded {isExcludedStrict && 'strict'}
            </span>
          </div>
        ) : null}
        <div className={styles.toggleContainer}>
          <Toggle
            label={!isInvoicingRejected ? 'Will charge' : "Won't charge"}
            className={styles.toggle}
            labelClass={
              !isInvoicingRejected ? styles.positiveLabel : styles.negativeLabel
            }
            checked={!isInvoicingRejected}
            handleChange={handleCheck}
            isEditable={isInvoicingRejected && isExcludedStrict ? false : isEditable}
          />
        </div>
        {lastReconciliationStatus === 'Pending' && !isInvoicingRejected && (
          <div className={styles.reconciliationRequestLabel}>
            <ReconciliationIcon /> Reconciliation request
          </div>
        )}
        {isInvoicingRejected && (
          <div className={styles.rejectReason}>
            <InfoIcon />
            {rejectionReasons[invoiceRejectReason]}
          </div>
        )}
      </div>
      {lastReconciliationStatus === 'Pending' && reconciliations.length > 0 && (
        <ReconciliationInfoCard
          onSetAsWontCharge={handleCheck}
          reconciliation={reconciliations[reconciliations.length - 1]}
          className={styles.reconciliationInfoCard}
          reloadResults={reloadResults}
        />
      )}
      <HoverCard {...props} className={styles.hoverCard} isExcluded={isExcluded} />
      <div className={styles.invitePath}>
        <Timeline>
          <PrevBookingPoint
            numberOfPriorsByName={numberOfPriorsByName}
            numberOfPriorsByPhoneAndName={numberOfPriorsByPhoneAndName}
            numberOfPriorsByPhone={numberOfPriorsByPhone}
            numberOfPriorsTotal={numberOfPriorsTotal}
            checkout={previousCheckOut}
            days={daysWithoutBookingUntilInvite}
            showAllPriorBookings={showAllPriorBookings}
            handleClick={() =>
              setGuestDataToInspect({
                bookingId: convertedSubscriberHotelBookingId,
                userData: {
                  convertedClientName,
                  convertedFormattedAddress,
                  convertedPhoneNumber,
                  convertedEmail,
                  checkIn,
                  checkOut
                }
              })
            }
            href={`/bookingHistory/${inviteSubscriptionId}/${moment(checkOut).format(
              'YYYY-MM-DD'
            )}`}
          />
          <Point primary={moment(invitedOn).format('MMM DD')} secondary="invited" />
          <BeforeBookingLine days={daysFromInviteUntilBooking} />
          <Point
            primary={moment(convertedOn).format('MMM DD')}
            secondary={`booked and ${convertedBookingStatus.toLowerCase()}`}
            color={convertedBookingStatus === 'Cancelled' ? 'deleted' : 'gray'}
          />
        </Timeline>
        <Counts click={clickCount} message={messageCount} call={callCount} />
      </div>
      <div className={styles.stayPath}>
        <Timeline>
          <Point primary={moment(checkIn).format('MMM DD')} secondary="check-in" />
          <Line form="solid" range={lengthOfStay + ' nights'} />
          <Point primary={moment(checkOut).format('MMM DD')} secondary="check-out" />
        </Timeline>
        <button
          className={styles.bookingEditButton}
          onClick={() => setIsShowingEditBookingDialog(true)}
        >
          Edit
        </button>
      </div>
      <div className={styles.bookingDetails}>
        <div className={styles.bookingDetailsHeader}>
          <div>
            <p className={styles.totalVal}>{formatPrice(convertedTotal)} </p>
            <p className={styles.roomName}>
              <span
                className={
                  isExcluded('ratecodefull', 'RateCodeStartsWith')
                    ? styles.blacklistedText
                    : ''
                }
              >
                {convertedRateCode} rate
              </span>
            </p>
          </div>
          <div className={styles.otaValues}>
            {isAgency && (
              <>
                <p className={styles.otaBookingIndicator}>OTA booking</p>
                <p className={styles.otaName}>{agencyName}</p>
              </>
            )}
          </div>
        </div>

        <ul className={styles.detailsList}>
          {convertedRateCodeKind && (
            <div className={styles.rateCategory}>
              <DetailListItem
                title="Rate category"
                val={convertedRateCodeKind}
              ></DetailListItem>
              <div className={styles.rateCategoryTooltip}>
                <p> Added by: {convertedRateCodeAddedBy}</p>
                <p>
                  Added on:{' '}
                  {moment(convertedRateCodeAddedOn).format('MMM DD, YYYY, hh:MM A')}
                </p>
              </div>
            </div>
          )}
          <DetailListItem
            isBlacklisted={isExcluded('trackinginfofull', 'TrackingInfoStartingWith')}
            title="Tracking"
            val={convertedTrackingCode}
          ></DetailListItem>
          <DetailListItem title="Booked by" val={convertedBookingUser}></DetailListItem>
          <DetailListItem title="Source" val={convertedBookingSource}></DetailListItem>
        </ul>
      </div>
    </div>
  )
}

const DetailListItem = ({ title, val, isBlacklisted }) => {
  const overflowProps = {}

  if (val && val.length > 18) {
    overflowProps.title = val
    overflowProps.className = styles.hovertip
  }

  return (
    <li {...overflowProps}>
      <span className={styles.listTitle}>{title}</span>
      <span
        className={[styles.listValue, isBlacklisted ? styles.blacklistedText : ''].join(
          ' '
        )}
      >
        {val}
      </span>
    </li>
  )
}

const rejectionReasons = {
  repeatGuest: 'Repeat guest',
  multipleBookings: 'Multiple bookings',
  wrongDates: 'Wrong dates',
  testBooking: 'Test booking',
  incompleteAddress: 'Incomplete address',
  streetNoMismatch: 'Street number mismatch',
  groupBooking: 'Group booking',
  corporateBooking: 'Corporate booking',
  notEnoughInfo: 'Not enough information',
  otaBooking: 'OTA Booking',
  longTimeAfterInvite: 'Long time after invite',
  hotelFriendsAndFamily: 'Hotel friends &amp; family'
}

export default memo(BillingCard)
